import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Search, 
  UserCog,
  Loader2,
  MoreVertical,
  Shield,
  Trash2,
  CheckCircle,
  XCircle,
  LogIn,
  Eye,
  EyeOff,
  Mail,
  Key,
  Lock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'team', label: 'Team Member' },
  { value: 'client', label: 'Client' },
  { value: 'client_team', label: 'Client Team Member' }
];

const Users = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders, impersonateUser, isImpersonating } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'team', organization_id: '', client_id: ''
  });

  // Password reset dialog
  const [resetTarget, setResetTarget] = useState(null); // { id, name, email }
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [resetting, setResetting] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const usersRes = await axios.get(`${API_URL}/users`, getAuthHeaders());
      setUsers(usersRes.data);

      if (isSuperAdmin) {
        const tenantsRes = await axios.get(`${API_URL}/tenants`, getAuthHeaders());
        setTenants(tenantsRes.data);
      }
      
      // Fetch clients for the dropdown
      const clientsRes = await axios.get(`${API_URL}/clients`, getAuthHeaders());
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        organization_id: isSuperAdmin ? formData.organization_id : user.organization_id
      };
      await axios.post(`${API_URL}/users`, data, getAuthHeaders());
      toast.success('User created successfully. They will receive login credentials.');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPassword.trim() || resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API_URL}/auth/admin-reset-password`, {
        email: resetTarget.email,
        password: resetPassword.trim()
      }, getAuthHeaders());
      toast.success(`Password updated for ${resetTarget.name || resetTarget.email}`);
      setResetTarget(null);
      setResetPassword('');
      setShowResetPwd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const generateResetPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let pwd = '';
    for (let i = 0; i < 12; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    setResetPassword(pwd);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API_URL}/users/${userId}`, getAuthHeaders());
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSendCredentials = async (userId, userName, userEmail) => {
    if (!window.confirm(`Send new login credentials to ${userName} (${userEmail})?\n\nThis will reset their password and send a new one via email.`)) return;
    
    try {
      const response = await axios.post(`${API_URL}/users/${userId}/send-credentials`, {}, getAuthHeaders());
      toast.success(response.data.message || 'Credentials sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send credentials');
    }
  };

  const handleLoginAs = async (targetUser) => {
    try {
      await impersonateUser(targetUser.id);
      toast.success(`Now viewing as ${targetUser.name}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as user');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'team',
      organization_id: '',
      client_id: ''
    });
  };

  const getTenantName = (tenantId) => {
    if (!tenantId) return 'No Tenant';
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.name || 'Unknown';
  };

  // Get clients filtered by selected organization
  const getFilteredClients = () => {
    const orgId = isSuperAdmin ? formData.organization_id : user?.organization_id;
    if (!orgId) return [];
    return clients.filter(c => c.organization_id === orgId);
  };

  // Check if client selection should be shown
  const showClientSelect = formData.role === 'client' || formData.role === 'client_team';

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Add and manage team members and clients</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-user-btn">
              <Plus size={18} className="mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  required
                  data-testid="user-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                  data-testid="user-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Password *</Label>
                  <Button type="button" variant="link" size="sm" onClick={generatePassword} className="h-auto p-0">
                    Generate
                  </Button>
                </div>
                <Input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter or generate password"
                  required
                  data-testid="user-password-input"
                />
                <p className="text-xs text-slate-500">
                  This password will be sent to the user. They can change it after first login.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value, client_id: '' })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
                    {roleOptions.filter(r => isSuperAdmin || r.value !== 'admin').map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label>Tenant *</Label>
                  <Select
                    value={formData.organization_id}
                    onValueChange={(value) => setFormData({ ...formData, organization_id: value, client_id: '' })}
                  >
                    <SelectTrigger data-testid="user-tenant-select">
                      <SelectValue placeholder="Select tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map(tenant => (
                        <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Client Selection - shown for client/client_team roles */}
              {showClientSelect && (
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  >
                    <SelectTrigger data-testid="user-client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {getFilteredClients().length === 0 ? (
                        <SelectItem value="_none" disabled>
                          {isSuperAdmin && !formData.organization_id 
                            ? "Select a tenant first" 
                            : "No clients available"}
                        </SelectItem>
                      ) : (
                        getFilteredClients().map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.company || client.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    This user will have access to this client&apos;s projects and data.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-user-btn">
                  Add User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-users-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {filteredUsers.length === 0 ? (
            <div className="p-12 text-center">
              <UserCog size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No users found</h3>
              <p className="text-slate-500 mt-1">Add your first team member or client</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    {isSuperAdmin && <th>Tenant</th>}
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-slate-600">
                              {u.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                          <Shield size={12} />
                          {u.role.replace('_', ' ')}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td className="text-slate-600">{getTenantName(u.organization_id)}</td>
                      )}
                      <td>
                        {u.is_verified ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-sm">
                            <CheckCircle size={14} />
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600 text-sm">
                            <XCircle size={14} />
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="text-slate-500 text-sm">
                        {u.last_login ? format(new Date(u.last_login), 'MMM d, h:mm a') : 'Never'}
                      </td>
                      <td>
                        {u.role !== 'super_admin' && u.id !== user.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-menu-${u.id}`}>
                                <MoreVertical size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {isSuperAdmin && !isImpersonating && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleLoginAs(u)}
                                    data-testid={`login-as-${u.id}`}
                                  >
                                    <LogIn size={14} className="mr-2" />
                                    Login as User
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              <DropdownMenuItem 
                                onClick={() => handleSendCredentials(u.id, u.name, u.email)}
                                data-testid={`send-credentials-${u.id}`}
                              >
                                <Key size={14} className="mr-2" />
                                Send Credentials
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => { setResetTarget(u); setResetPassword(''); setShowResetPwd(false); }}
                                className="text-blue-600 focus:text-blue-600"
                              >
                                <Lock size={14} className="mr-2" />
                                Set Password
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(u.id)}
                                className="text-red-600"
                              >
                                <Trash2 size={14} className="mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Set Password Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => { if (!open) { setResetTarget(null); setResetPassword(''); setShowResetPwd(false); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={18} className="text-blue-600" />
              Set New Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-2">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
              <p className="text-sm font-medium text-slate-800">{resetTarget?.name}</p>
              <p className="text-xs text-slate-500">{resetTarget?.email}</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>New Password</Label>
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs text-blue-600"
                  onClick={generateResetPassword}>
                  Generate
                </Button>
              </div>
              <div className="relative">
                <Input
                  type={showResetPwd ? 'text' : 'password'}
                  value={resetPassword}
                  onChange={e => setResetPassword(e.target.value)}
                  placeholder="Enter new password (min 6 chars)"
                  required
                  autoFocus
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowResetPwd(!showResetPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showResetPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-xs text-slate-400">
                This immediately sets the password. The user can log in right away.
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1"
                onClick={() => { setResetTarget(null); setResetPassword(''); }}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={resetting || !resetPassword.trim()}>
                {resetting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Lock size={14} className="mr-1" />}
                Set Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
